import React from "react";
import { motion } from "framer-motion";
import { Edit3, Zap, Copy } from "lucide-react";

export default function HowToUse() {
  const steps = [
    {
      icon: <Edit3 className="w-8 h-8 text-indigo-500" />,
      title: "Type a few random words",
      description: "Input your keywords or phrases to start generating captions & hashtags.",
    },
    {
      icon: <Zap className="w-8 h-8 text-indigo-500" />,
      title: "AI generates captions & hashtags",
      description: "Our AI instantly turns your words into engaging captions and trending hashtags.",
    },
    {
      icon: <Copy className="w-8 h-8 text-indigo-500" />,
      title: "Copy & use directly in your posts",
      description: "Easily copy the generated content and use it on Instagram, X, or any social media platform.",
    },
  ];

  return (
    <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
      <motion.h2
        className="text-3xl sm:text-4xl font-bold text-center mb-12 text-slate-900 dark:text-white"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        How QuickCaptionIO Works
      </motion.h2>

      {/* Steps Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {steps.map((step, idx) => (
          <motion.div
            key={idx}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 flex flex-col items-center text-center hover:scale-105 hover:shadow-2xl transition-transform duration-300"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: idx * 0.2 }}
          >
            <div className="mb-4">{step.icon}</div>
            <h3 className="font-semibold text-xl mb-2 text-slate-900 dark:text-white">{step.title}</h3>
            <p className="text-slate-600 dark:text-slate-300">{step.description}</p>
          </motion.div>
        ))}
      </div>

      {/* Use Case Example */}
      <motion.div
        className="mt-16 bg-indigo-50 dark:bg-gray-900 rounded-2xl p-6 md:p-12 text-center shadow-md"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.8 }}
      >
        <h3 className="font-bold text-2xl mb-4 text-slate-900 dark:text-white">Example Use Case</h3>
       <p className="text-lg text-slate-700 dark:text-slate-300 max-w-2xl mx-auto mb-10">
  Imagine a travel vlogger visiting <span className="font-semibold">Japan for the very first time</span>.  
  They simply type in words like <span className="font-semibold">“Tokyo Skytree, sushi, first trip”</span> →  
  our AI instantly generates an engaging caption such as  
  <span className="italic text-slate-900 dark:text-white">“Exploring Japan’s beauty from the top of Tokyo Skytree 🗼🍣  
  First trip, endless memories!”</span>  
  along with trending hashtags like  
  <span className="text-indigo-500 font-semibold">#TokyoAdventures #JapanTrip #TravelVlog</span>.
</p>

      </motion.div>
    </section>
  );
}
